// Purpose: Tabs primitive with URL-synced active tab state. Tab
// selection persists as ?tab=<id> so deep links land on the right view
// (admin Settings, admin Routing, admin Forensics tabs).
//
// Visual: horizontal tab row, active tab shown as accent-primary bottom
// border + fg-primary label; inactive tabs as fg-secondary with hover
// to fg-primary. Content region is the consumer's responsibility —
// Tabs only renders the row + exposes `activeTab`.

import { useCallback } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface TabItem {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
}

export interface TabsProps {
  items: TabItem[];
  /** Default tab when ?tab= isn't set. Defaults to first item. */
  defaultTab?: string | undefined;
  /** Query-string key. Defaults to "tab". Use distinct keys when two
      Tabs instances share a page. */
  paramKey?: string | undefined;
  className?: string | undefined;
}

export function useTabs({
  items,
  defaultTab,
  paramKey = 'tab',
}: Pick<TabsProps, 'items' | 'defaultTab' | 'paramKey'>) {
  const [params, setParams] = useSearchParams();
  const fallback = defaultTab ?? items[0]?.id ?? '';
  const paramValue = params.get(paramKey);
  const activeTab = paramValue && items.some((t) => t.id === paramValue) ? paramValue : fallback;

  const setTab = useCallback(
    (id: string) => {
      const next = new URLSearchParams(params);
      if (id === fallback) {
        next.delete(paramKey);
      } else {
        next.set(paramKey, id);
      }
      setParams(next, { replace: true });
    },
    [params, setParams, paramKey, fallback]
  );

  return { activeTab, setTab };
}

export function Tabs({ items, defaultTab, paramKey = 'tab', className }: TabsProps) {
  const { activeTab, setTab } = useTabs({ items, defaultTab, paramKey });
  return (
    <div
      role="tablist"
      className={['flex items-center gap-1 border-b border-border-default', className ?? ''].join(
        ' '
      )}
    >
      {items.map((item) => {
        const isActive = item.id === activeTab;
        return (
          <button
            key={item.id}
            role="tab"
            type="button"
            aria-selected={isActive}
            onClick={() => setTab(item.id)}
            className={[
              'inline-flex items-center gap-2 h-10 px-4',
              'text-body-m font-semibold',
              'border-b-2 -mb-px',
              'transition-colors duration-fast ease-out',
              isActive
                ? 'border-accent-primary text-fg-primary'
                : 'border-transparent text-fg-secondary hover:text-fg-primary',
            ].join(' ')}
          >
            {item.icon}
            <span>{item.label}</span>
          </button>
        );
      })}
    </div>
  );
}
